// The browser side of /api/offers: the product Worker (worker.js via lib/offers-api.ts) and the partner
// demo's Worker answer the same three routes.
import type { OfferDecision, OfferDoc, OfferState, OfferStatus } from '../../lib/offers'

export type OfferReply = Omit<OfferState, 'createdBy'> & { status: OfferStatus }
export type OfferDraft = Omit<OfferDoc, 'id' | 'ref' | 'createdAt' | 'sample' | 'total'>

/**
 * Where the offer API and the customer pages live: the site this code was loaded from. On
 * freightratesmcp.com (and on the partner demo's own host) that is the page's origin. Inside the Navo24
 * workspace the desk is a federated module served from freightratesmcp.com, so the calls and the links go
 * there, not to the workspace host, which has no offers.
 */
export function offerOrigin(): string {
  try {
    const o = new URL(import.meta.url).origin
    if (/^https?:\/\//.test(o)) return o
  } catch {
    // Not a URL we can read: fall back to the page.
  }
  return window.location.origin
}

export class OfferApiError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message)
  }
}

async function call(path: string, init?: RequestInit): Promise<OfferReply> {
  const res = await fetch(`${offerOrigin()}${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  })
  const body = (await res.json().catch(() => ({}))) as OfferReply & { error?: string }
  if (!res.ok) throw new OfferApiError(body.error ?? `HTTP ${res.status}`, res.status)
  return body
}

/** Publish an offer. The product requires the seller's session token; the partner demo takes none. */
export const publishOffer = (draft: OfferDraft, token?: string | null) =>
  call('/api/offers', {
    method: 'POST',
    body: JSON.stringify(draft),
    ...(token ? { headers: { authorization: `Bearer ${token}` } } : {}),
  })

/** The customer's open counts as a view; the seller's check (`peek`) does not. */
export const fetchOffer = (id: string, peek = false) =>
  call(`/api/offers/${id}${peek ? '?peek=1' : ''}`)

export const sendDecision = (id: string, d: Omit<OfferDecision, 'at'>) =>
  call(`/api/offers/${id}/decision`, { method: 'POST', body: JSON.stringify(d) })
